import "@mantine/core/styles.css";
// import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/spotlight/styles.css";
import "./styles/global.scss";
import "dayjs/locale/en-gb";
import "dayjs/locale/fr";

import {
  ActionIcon,
  Button,
  createTheme,
  em,
  MantineColorsTuple,
  MantineProvider,
  Menu,
  Modal,
  Paper,
  Group,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput, DatePickerInput, DatesProvider } from "@mantine/dates";
import { useDocumentTitle } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { useEffect, useState } from "react";

import { useTranslation } from "~/hooks/use-typed-translation";
import i18nextServer, { localeCookie } from "~/localization/i18n.server";

import { Footer } from "./components/footer";
import { Navbar } from "./components/navbar";
import { PartnerFooter } from "./components/partner-footer";
import i18n from "./localization/i18n";
import NotFoundPage from "./routes/$/route";
import { getPartnerProfileBySlug } from "./services/get-partner-profile-by-slug";
import {
  getCurrencyFromCookie,
  serializeCurrencyCookie,
} from "./utils/currency.server";
import { getCurrencyAndFxRate } from "./utils/get-currency-fx-rate";
import {
  AnalyticsPreferences,
  getAnalyticsPreferences,
  PREFERENCES_EVENT,
  saveAnalyticsPreferences,
} from "./utils/analytics-preferences.client";


// From logo color
const primary: MantineColorsTuple = [
  "#e2fdfd",
  "#d4f6f4",
  "#afe8e7",
  "#88dad9",
  "#66cfcd",
  "#4fc7c5",
  "#40c4c2",
  "#2dadab",
  "#199a98",
  "#008684",
];

const accent: MantineColorsTuple = [
  "#f0eaff",
  "#dacfff",
  "#b19cff",
  "#8664ff",
  "#6136ff",
  "#4a18ff",
  "#3d07ff",
  "#2f00e5",
  "#2800cd",
  "#1c00b5",
];

function AnalyticsScripts() {
  useEffect(() => {
    const isProductionHost =
      window.location.host.includes("appointment.laruche.health") ||
      window.location.host.includes("rendezvous.laruche.health");

    if (!isProductionHost) return;

    const load = (preferences: AnalyticsPreferences) => {

    if (preferences.analytics && !document.documentElement.dataset.clarityLoaded) {
      document.documentElement.dataset.clarityLoaded = "true";
      const clarityWindow = window as Window & {
      clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
      };
      clarityWindow.clarity = clarityWindow.clarity || function (...args: unknown[]) {
        const clarity = clarityWindow.clarity;
        if (!clarity) return;
        clarity.q = clarity.q || [];
        clarity.q.push(args);
      };

      const clarityScript = document.createElement("script");
      clarityScript.async = true;
      clarityScript.src = "https://www.clarity.ms/tag/oucxsha8s0";
      clarityScript.dataset.analytics = "clarity";
      document.head.appendChild(clarityScript);
    }

    type MetaPixelFunction = ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      loaded: boolean;
      version: string;
    };

    if (preferences.marketing && !document.documentElement.dataset.metaLoaded) {
      document.documentElement.dataset.metaLoaded = "true";
      const pixelWindow = window as Window & {
      fbq?: MetaPixelFunction;
      _fbq?: MetaPixelFunction;
      };

      if (!pixelWindow.fbq) {
      const fbq = function (...args: unknown[]) {
        if (fbq.callMethod) {
          fbq.callMethod(...args);
        } else {
          fbq.queue.push(args);
        }
      } as MetaPixelFunction;
      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = "2.0";
      pixelWindow.fbq = fbq;
      pixelWindow._fbq = fbq;

      const pixelScript = document.createElement("script");
      pixelScript.async = true;
      pixelScript.src = "https://connect.facebook.net/en_US/fbevents.js";
      pixelScript.dataset.analytics = "meta-pixel";
      document.head.appendChild(pixelScript);
      }

      pixelWindow.fbq?.("consent", "grant");
      pixelWindow.fbq?.("init", "398839189989152");
      pixelWindow.fbq?.("track", "PageView");
    }
    };
    load(getAnalyticsPreferences());
    const onPreferences = (event: Event) =>
      load((event as CustomEvent<AnalyticsPreferences>).detail);
    window.addEventListener(PREFERENCES_EVENT, onPreferences);
    return () => window.removeEventListener(PREFERENCES_EVENT, onPreferences);
  }, []);

  return null;
}

function AnalyticsPreferencesBanner() {
  const [preferences, setPreferences] = useState<AnalyticsPreferences | null>(null);
  useEffect(() => setPreferences(getAnalyticsPreferences()), []);
  if (!preferences || preferences.explicit) return null;
  const save = () => setPreferences(saveAnalyticsPreferences(preferences));
  return (
    <Paper withBorder shadow="md" p="md" radius="md" pos="fixed" bottom={16} left={16} right={16} style={{ zIndex: 10000 }}>
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={6}>
          <Text fw={600}>Privacy preferences</Text>
          <Text fz={13} c="dimmed">First-party booking attribution remains operational. Optional vendors can be changed here.</Text>
          <Group>
            <Switch label="Analytics (Clarity)" checked={preferences.analytics} onChange={event => setPreferences({ ...preferences, analytics: event.currentTarget.checked })} />
            <Switch label="Marketing (Meta)" checked={preferences.marketing} onChange={event => setPreferences({ ...preferences, marketing: event.currentTarget.checked })} />
          </Group>
        </Stack>
        <Button onClick={save}>Save preferences</Button>
      </Group>
    </Paper>
  );
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  console.log("[root loader] params:", params, "url:", request.url);
  const lang = params?.lang ?? "";
  const url = new URL(request.url);
  const partnerSlug = url.pathname.match(/\/partners\/([^\/]+)/)?.[1] ?? "";
  let partnerRes = null;

  if (partnerSlug) {
    partnerRes = await getPartnerProfileBySlug(partnerSlug);
  }

  // Currency & FX Rate Logic
  const detectedCountry =
    request?.headers?.get("x-vercel-ip-country")?.toLowerCase() ?? "ci";
  let currencyContext = await getCurrencyFromCookie(request);
  let newCookieValue = null;

  // Cache busting: If country in cookie mismatches detected country, re-fetch
  if (!currencyContext || currencyContext.country !== detectedCountry) {
    try {
      const freshContext = await getCurrencyAndFxRate(request);
      currencyContext = {
        currency: freshContext.currency,
        fxRate: freshContext.fxRate,
        country: freshContext.country,
      };
      newCookieValue = await serializeCurrencyCookie(currencyContext);
    } catch (error) {
      console.error("[root loader] Error fetching currency context:", error);
      // Explicit, intentional fallback
      currencyContext = { currency: "XOF", fxRate: 1, country: "ci" };
    }
  }

  if (
    !lang ||
    (lang && i18n.supportedLngs.includes(lang.toLocaleLowerCase()))
  ) {
    const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
      ? lang
      : await i18nextServer.getLocale(request);

    const headers = new Headers();
    headers.append("Set-Cookie", await localeCookie.serialize(locale));
    if (newCookieValue) {
      headers.append("Set-Cookie", newCookieValue);
    }

    return json(
      {
        locale,
        partnerData: partnerRes?.data ?? null,
        currency: currencyContext?.currency ?? "XOF",
        fxRate: currencyContext?.fxRate ?? 1,
        country: currencyContext?.country ?? "ci",
      },
      { headers }
    );
  }

  // If lang is provided but not supported, throw 404
  throw new Response("Language not supported", { status: 404 });
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Get the locale from the loader using useRouteLoaderData to avoid throwing in ErrorBoundary
  const data = useRouteLoaderData<typeof loader>("root");
  const navigation = useNavigation();
  const isNavigating = navigation.state !== "idle";

  const { i18n } = useTranslation();

  // Rigorous check for colorPalette to avoid Mantine theme crashes
  const newPrimary =
    Array.isArray(data?.partnerData?.colorPalette) && data?.partnerData?.colorPalette.length === 10
      ? (data.partnerData.colorPalette as unknown as MantineColorsTuple)
      : null;

  const themeOverride = createTheme({
    // These should be in sync with the values in styles/utilities.module.scss
    breakpoints: {
      xs: em(414),
      sm: em(768),
      md: em(1024),
      lg1: em(1200),
      lg2: em(1440),
      lg3: em(1600),
      lg4: em(1920),
    },
    primaryColor: "primary",
    black: newPrimary
      ? newPrimary[9]
      : "hsl(242, 98%, 16%)",
    colors: {
      primary: newPrimary || primary,
      accent,
    },
    fontFamily: "Poppins, sans-serif",
    components: {
      Text: Text.extend({
        defaultProps: { lh: 1.4 },
      }),
      TextInput: TextInput.extend({
        classNames: {
          input: "mantine-text-input",
          root: "mantine-text-input-wrapper",
        },
      }),
      Button: Button.extend({
        defaultProps: { loaderProps: { type: "dots" } },
        classNames: {
          root: "mantine-button",
        },
      }),
      ActionIcon: ActionIcon.extend({
        defaultProps: { loaderProps: { type: "dots" } },
      }),
      Menu: Menu.extend({
        defaultProps: { transitionProps: { transition: "pop" } },
      }),
      Tabs: Tabs.extend({
        classNames: {
          tab: "mantine-tab",
        },
      }),

      DatePickerInput: DatePickerInput.extend({
        defaultProps: {
          valueFormat: "DD/MM/YYYY",
          highlightToday: true,
          styles: {
            day: {
              color: "inherit",
            },
          },
        },
        classNames: {
          input: "mantine-date-picker-input",
          root: "mantine-date-picker-input-wrapper",
        },
      }),
      DateInput: DateInput.extend({
        defaultProps: {
          valueFormat: "DD/MM/YYYY",
          // Keep calendar and parsing in DD/MM/YYYY while typing
          dateParser: (value: string) => {
            const sanitized = value.trim();
            if (!sanitized) return null;

            const parts = sanitized.split("/").map(p => p.trim());
            const now = new Date();

            const parseTwoDigitYear = (yy: number) => {
              // interpret 00-99 as 2000-2099 to avoid 1900s
              return 2000 + yy;
            };

            // If we only have day or day/month partially, avoid US parsing by returning a best-effort Date
            if (parts.length === 1) {
              const day = Number(parts[0]);
              if (Number.isNaN(day)) return null;
              return new Date(
                now.getFullYear(),
                now.getMonth(),
                Math.min(Math.max(day, 1), 31)
              );
            }

            if (parts.length === 2) {
              const day = Number(parts[0]);
              const month = Number(parts[1]);
              if (Number.isNaN(day) || Number.isNaN(month)) return null;
              return new Date(
                now.getFullYear(),
                Math.min(Math.max(month, 1), 12) - 1,
                Math.min(Math.max(day, 1), 31)
              );
            }

            // parts.length >= 3
            const day = Number(parts[0]);
            const month = Number(parts[1]);
            let year = Number(parts[2]);
            if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year))
              return null;
            if (year < 100) year = parseTwoDigitYear(year);
            return new Date(
              year,
              Math.min(Math.max(month, 1), 12) - 1,
              Math.min(Math.max(day, 1), 31)
            );
          },
          placeholder: "DD/MM/YYYY",
          // highlightToday: true,
          styles: {
            day: {
              color: "inherit",
            },
          },
        },
        classNames: {
          input: "mantine-date-picker-input",
          root: "mantine-date-picker-input-wrapper",
        },
      }),
      Modal: Modal.extend({
        styles: {
          header: {},
          body: {
            maxHeight: 600 - 60,
            minHeight: 450 - 60,
            // display: "flex",
          }, //* 60 is the modal header height, convert to CSS variable later
        },
      }),
    },
  });

  return (
    <html lang={data?.locale || "en"} dir={i18n.dir()}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />

        {/* <!-- Google font --> */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+Mono:wght@100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest"></link>
        <Links />
      </head>
      <body>
        <MantineProvider theme={themeOverride}>
          <div
            aria-hidden="true"
            className="route-loading-bar"
            data-active={isNavigating ? "true" : "false"}
          />
          <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <Navbar />
            <DatesProvider
              settings={{
                locale: data?.locale === "fr" ? "fr" : "en-gb",
                firstDayOfWeek: 1,
              }}>
              <ModalsProvider>{children}</ModalsProvider>
            </DatesProvider>
            {data?.partnerData ? <PartnerFooter /> : <Footer />}
          </div>
          {/*
            Temporarily hidden. Keep the privacy-preferences banner component
            and preference implementation available for a later release.
            <AnalyticsPreferencesBanner />
          */}
        </MantineProvider>
        <AnalyticsScripts />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();

  useDocumentTitle("An error may have occurred");

  if (isRouteErrorResponse(error)) {
    const errorData = error.data;

    if (error.status === 404) {
      return <NotFoundPage />;
    } else
      return (
        <main>
          <p>
            {error.data?.message ||
              "Something went wrong. Please try again later"}
          </p>
          {errorData?.details && <p>Details: {errorData.details}</p>}
          <p>
            Back to{" "}
            <Link reloadDocument to="/">
              safety
            </Link>
          </p>
        </main>
      );
  }

  return (
    <main>
      <p>An unexpected error occurred.</p>
      <p>
        Back to{" "}
        <Link reloadDocument to="/">
          safety
        </Link>
      </p>
    </main>
  );
}
